import styled from 'styled-components';
import { Box, TextareaAutosize } from '@mui/material';

import colors from '../../styles/colors';
import LoadingPage from '../LoadingPage';

const Header = styled.div`
  width: 100%;
  padding: 10px;
  background-color: ${colors.base.secondary2};
  color: ${colors.base.alpha};
  min-height: 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
`;

const HideButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  & > svg {
    color: ${colors.base.alpha};
  }
`;

const Button = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: bold;
  line-height: 12px;
  padding: ${(props) => props.padding || '10px 14px'};
  border-radius: 5px;
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  border: none;
  color: ${(props) => props.color || 'white'};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  /* Icon positioning */
  .icon-left {
    order: -1; /* Moves icon to the left */
    width: 18px;
    height: 18px;
  }

  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
    background-color: ${colors.shades.gamma12};
    color: ${colors.shades.gamma11};
    cursor: not-allowed;
  }
`;

const EndButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 10px;
  margin-top: auto;
  padding: 12px 10px;
`;

const StyledBox = styled(Box)`
  width: 100%;
  background-color: #ffffff;
  padding: 12px;
  border: 1px solid ${colors.shades.secondary7};
  border-radius: 6px;
`;

const ResizableBox = styled(StyledBox)`
  overflow: auto;
  resize: vertical;
  min-height: ${(props) => props.minHeight || '50px'};
  max-block-size: max-content;
`;

const PromptBox = styled(StyledBox)`
  position: relative;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 100px;
  flex-grow: 1;
`;

const SmartContextContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const SmartContextMessage = styled(TextareaAutosize)(({ readOnly }) => ({
  flexGrow: 1,
  width: '100%',
  resize: 'none',
  padding: '8px',
  border: readOnly ? 'none' : `1px solid ${colors.shades.gamma3}`,
  outline: readOnly ? 'none' : 'auto',
  backgroundColor: readOnly ? colors.shades.alpha2 : colors.base.alpha,
  borderRadius: '4px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
}));

const Body = styled.div`
  padding: 10px 10px 0px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 0;
  min-height: 0;
  /* header = 56px, buttons = 62px */
  flex-basis: calc(100% - 56px - 62px);
  gap: 10px;
`;

const Loader = styled(LoadingPage)`
  flex-grow: 1;
`;

const Title = styled.h2`
  font-size: 10px;
  font-weight: 500;
  color: ${colors.base.secondary};
  margin-bottom: 6px;
  margin-top: 0;
`;

const Reason = styled.p`
  font-size: 12.5px;
  font-weight: 400;
  color: ${colors.base.secondary2};
  margin-bottom: 0;
  margin-top: 0;
`;

const ClientFacts = styled(Box)`
  display: flex;
  flex-grow: 1;
  gap: 2px;
  flex-direction: column;
`;

const DisplayToggleWrapper = styled.div`
  font-size: 12.5px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 6px;

  label {
    font-weight: bold;
  }

  select {
    font-size: 12.5px;
  }
`;

const InsightsGained = styled.div`
  font-size: 12.5px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  & > div {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  & > div > div:first-child {
    font-weight: bold;
    font-size: 10px;
  }
`;

const Badge = styled.span`
  background-color: ${colors.base.secondary2};
  color: ${colors.base.alpha};
  font-size: 10px;
  padding: 6px 8px;
  border-radius: 20px;
  margin-left: 12px;
  font-weight: normal;
  cursor: pointer;
  &::before {
    content: "✨";
    font-size: 10px;
    margin-right: 4px;
  }
`;

const AdditionalQueryContainer = styled.div`
  position: absolute;
  bottom: 12px;
  left: 0px;
`;

const QueryContent = styled.div`
  max-height: 100px;
  overflow: auto;
  font-size: 12.5px;
  font-weight: 400;
  margin-left: 12px;
  margin-top: 12px;
`;

export {
  Header,
  HideButton,
  Button,
  ResizableBox,
  PromptBox,
  SmartContextContainer,
  SmartContextMessage,
  Body,
  Loader,
  Title,
  Reason,
  ClientFacts,
  DisplayToggleWrapper,
  InsightsGained,
  EndButtonContainer,
  StyledBox,
  Badge,
  AdditionalQueryContainer,
  QueryContent,
};
